import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  MessageCircle, 
  Mail, 
  Calendar,
  User, 
  Tag,
  Bell,
  ListChecks,
  Move,
  Copy,
  Clock,
  Facebook,
  FileSpreadsheet,
  X as CloseIcon,
  Search,
  Zap
} from 'lucide-react';
import { automationActionData } from "../../utils/globalConstants"; // Use existing data source
import './ActionSelectionPopup.css';

// Define categories for better organization - but use existing automationActionData
const ACTION_CATEGORIES = {
  COMMUNICATION: '💬 Connect & Engage',
  LEAD_MANAGEMENT: '🎯 Organize & Qualify', 
  TASKS: '⚡ Follow-up & Notify',
  SALES: '📊 Export & Retarget',
  SYSTEM: '⏱️ Timing & Flow'
};

interface ActionSelectionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
  currentValue: string;
  actionData: any[]; // Use existing automationActionData structure
}

const ActionSelectionPopup: React.FC<ActionSelectionPopupProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentValue,
  actionData
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredActions, setFilteredActions] = useState(actionData);

  // Filter actions based on search query - using existing data structure
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredActions(actionData);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = actionData.filter(
      action => 
        action.name.toLowerCase().includes(query) || 
        action.value.toLowerCase().includes(query)
    );
    
    setFilteredActions(filtered);
  }, [searchQuery, actionData]);

  // Categorize actions based on their value - using existing structure
  const categorizeAction = (actionValue: string): string => {
    if (actionValue.includes('whatsapp') || actionValue.includes('email') || actionValue.includes('gmail') || actionValue.includes('zeptomail')) {
      return ACTION_CATEGORIES.COMMUNICATION;
    } else if (actionValue.includes('status') || actionValue.includes('label') || actionValue.includes('assign') || actionValue.includes('list')) {
      return ACTION_CATEGORIES.LEAD_MANAGEMENT;
    } else if (actionValue.includes('task') || actionValue.includes('notification')) {
      return ACTION_CATEGORIES.TASKS;
    } else if (actionValue.includes('google') || actionValue.includes('facebook')) {
      return ACTION_CATEGORIES.SALES;
    } else {
      return ACTION_CATEGORIES.SYSTEM;
    }
  };

  // Get icon for action - using existing value structure
  const getActionIcon = (actionValue: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'send_whatsapp': <MessageCircle size={22} />,
      'send_email': <Mail size={22} />,
      'send_gmail': <Mail size={22} />,
      'send_zeptomail': <Mail size={22} />,
      'create_task': <Calendar size={22} />,
      'status_change': <ListChecks size={22} />,
      'label_change': <Tag size={22} />,
      'assign_lead': <User size={22} />,
      'push_notification': <Bell size={22} />,
      'move_to_list': <Move size={22} />,
      'copy_to_list': <Copy size={22} />,
      'delay': <Clock size={22} />,
      'add_to_google_sheet': <FileSpreadsheet size={22} />,
      'add_facebook_audience': <Facebook size={22} />
    };

    return iconMap[actionValue] || <Zap size={22} />;
  };

  // Get description for action - using existing value structure
  const getActionDescription = (actionValue: string): string => {
    const descriptionMap: { [key: string]: string } = {
      'send_whatsapp': 'Instantly connect with leads through personalized WhatsApp messages that get opened and read',
      'send_email': 'Send beautiful, professional emails that convert prospects into customers with Brevo\'s proven templates',
      'send_gmail': 'Reach leads directly from your Gmail account with warm, personal messages that build relationships',
      'send_zeptomail': 'Deliver critical transactional emails with 99.9% delivery rates for important communications',
      'create_task': 'Never miss a follow-up again - automatically create tasks to keep your sales process moving forward',
      'status_change': 'Keep your pipeline organized by automatically moving leads through your sales stages',
      'label_change': 'Smart lead categorization that helps you segment and target the right prospects at the right time',
      'assign_lead': 'Distribute leads fairly across your team for maximum conversion and faster response times',
      'push_notification': 'Get instant alerts on your phone when hot leads take action - never miss an opportunity',
      'move_to_list': 'Organize leads into targeted lists for laser-focused marketing campaigns that convert',
      'copy_to_list': 'Add high-value leads to multiple campaigns without losing them from your main pipeline',
      'delay': 'Perfect your timing - wait for the optimal moment before sending your next message',
      'add_to_google_sheet': 'Export lead data automatically for reporting, analysis, and team collaboration',
      'add_facebook_audience': 'Turn your best leads into powerful Facebook lookalike audiences for explosive growth'
    };

    return descriptionMap[actionValue] || 'Automate this action to save time and increase conversions';
  };

  // Group actions by category - using existing data structure
  const groupedActions = filteredActions.reduce((acc, action) => {
    const category = categorizeAction(action.value);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(action);
    return acc;
  }, {} as Record<string, any[]>);

  // Handle action selection - using existing onSelect callback
  const handleActionSelect = (value: string) => {
    onSelect(value); // Call existing onSelect function exactly as before
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="action-popup-backdrop" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="action-popup-content">
        {/* Header */}
        <div className="action-popup-header">
          <h2>Choose Action</h2>
          <button className="action-popup-close" onClick={onClose}>
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Introduction text */}
        <div className="action-intro">
          <p>
            <strong>Automate your workflow!</strong> Choose from powerful actions that will execute automatically when your trigger conditions are met. Build sophisticated automation sequences by chaining multiple actions together.
          </p>
        </div>
        
        {/* Search */}
        <div className="action-search-container">
          <div className="action-search-input">
            <Search className="action-search-icon" size={18} />
            <input
              type="text"
              placeholder="Search for actions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="action-search-clear" 
                onClick={() => setSearchQuery('')}
              >
                <CloseIcon size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Body with categories - using existing automationActionData */}
        <div className="action-popup-body">
          {Object.keys(groupedActions).length > 0 ? (
            Object.entries(groupedActions).map(([category, actions], categoryIndex) => (
              <div key={`category-${categoryIndex}-${category}`} className="action-category-section">
                <h3 className="action-category-title">{category}</h3>
                <div className="action-grid">
                  {(actions as any[]).map((action: any, actionIndex: number) => (
                    <div
                      key={`action-${categoryIndex}-${actionIndex}-${action.value}`}
                      className={`action-card ${currentValue === action.value ? 'selected' : ''}`}
                      data-action={action.value}
                      onClick={() => handleActionSelect(action.value)}
                    >
                      <div className="action-card-icon">
                        <div className="icon-placeholder">
                          {getActionIcon(action.value)}
                        </div>
                      </div>
                      <div className="action-card-content">
                        <h4 className="action-card-title">
                          {action.name} {/* Use existing name from automationActionData */}
                        </h4>
                        <p className="action-card-description">
                          {getActionDescription(action.value)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="action-empty-state">
              <p>No matching actions found</p>
              <button onClick={() => setSearchQuery('')}>Clear search</button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="action-popup-footer">
          <button 
            className="action-btn-secondary" 
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ActionSelectionPopup;